import { Controller } from '@hotwired/stimulus'
import moment from 'moment';
import { debounce, divide, filter, find, forEach, last, sortBy, template } from 'lodash';

import Address from '../../models/address';
import { closeFlashAlert, flashAlert } from '../../shared/flash'
export default class extends Controller {

  static targets = [
    'checkout',
    'container',
    'items',
    'cartDetail',
    'cartDetailTemplate',
    'cartLoading',
    'checkoutBtn',
    'deliveryInfo',
    'deliveryTemplate',
    'submitOrderBtn',
    'discountContainer',
    'promoContainer',
    'cartItemTemplate',
    'bookingTemplate',
    'retailTemplate',
    'itemMenuTemplate',
  ]

  initialize() {
    this.products = []
    this.moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    this.onValidateCheckoutForm = debounce(this.onValidateCheckoutForm, 100)
    this.cartDetailTemplate = template(this.cartDetailTemplateTarget.innerHTML)
    this.cartItemTemplate = template(this.cartItemTemplateTarget.innerHTML)
    this.bookingTemplate = template(this.bookingTemplateTarget.innerHTML)
    this.retailTemplate = template(this.retailTemplateTarget.innerHTML)
    this.itemMenuTemplate = template(this.itemMenuTemplateTarget.innerHTML)
    this.deliveryTemplate = template(this.deliveryTemplateTarget.innerHTML)
  }

  connect() {
    this.fetchHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    }
    this.cart_context = this.containerTarget.dataset.context
    this.contextContainer = document.querySelector(`#${this.cart_context}-container`)
    this.orderId = this.containerTarget.dataset.orderId;
    this.destinationId = this.containerTarget.dataset.destinationId
    this.displayOrderTotals()

    if (this.cart_context === 'create') {
      this.contextContainer = document
      this.contextContainer.addEventListener('productsLoaded', this.onProductsLoaded)
      this.contextContainer.addEventListener('cartAddSuccess', this.onCartAdd)
    }

    if (this.cart_context === 'checkout') {
      this.getDestinationProducts()
      this.contextContainer.addEventListener('tipUpdate', this.onTipUpdate)
      this.contextContainer.addEventListener('validateCheckoutForm', this.onValidateCheckoutForm)
      this.contextContainer.addEventListener('intentUpdateStart', this.onIntentUpdateStart)
      this.contextContainer.addEventListener('promotionChange', this.onPromotionChange)
    }

    document.addEventListener('priceModification', this.onPriceModification)

    this.getOrder()
  }

  onCartAdd = _event => {
    this.getOrder()
  }

  onPromotionChange = _event => {
    this.getOrder()
  }

  onProductsLoaded = event => {
    this.products = event?.detail?.products
    this.buildCart();
  }

  onTipUpdate = () => {
    this.getOrder()
      .then(() => {
        this.dispatchAmountChange()
      })
      .catch(err => this.handleError(err))
  }

  onPriceModification = () => {
    this.getOrder()
      .then(() => {
        const detail = { detail: { priceOverride: true } }
        const amountChangeEvent = new CustomEvent('amountChange', detail)
        this.contextContainer.dispatchEvent(amountChangeEvent)
      })
      .catch(err => this.handleError(err))
  }

  onValidateCheckoutForm = event => {
    this.submitEnabled = !!event?.detail?.valid
    this.toggleSubmit()
  }

  onIntentUpdateStart = _event => {
    this.submitEnabled = false
    this.toggleSubmit()
  }

  toggleSubmit() {
    this.submitOrderBtnTarget.disabled = !this.submitEnabled
  }

  startLoading = () => {
    this.submitOrderBtnTarget.disabled = true
    this.cartLoadingTarget.classList.remove('hidden');
    this.itemsTarget.classList.add('hidden');
  }

  endLoading = () => {
    this.cartLoadingTarget.classList.add('hidden');
    this.itemsTarget.classList.remove('hidden');
    this.toggleSubmit()
  }

  handleError(err) {
    console.error(err)
    const errorMessage = err?.message || err
    flashAlert(errorMessage)
    this.endLoading()
  }

  submitOrder = () => {
    const submitCartOrderEvent = new CustomEvent('submitOrder')
    this.contextContainer.dispatchEvent(submitCartOrderEvent)
  }

  getDestinationProducts() {
    if (!this.destinationId) {
      return [];
    }

    fetch(`/sandbar/admin/admin_products?destination_id=${this.destinationId}`)
      .then(resp => {
        if (resp.ok) {
          return resp.json()
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .then(products => {
        this.products = products
        this.buildCart()
      })
      .catch(err => this.handleError(err))
  }

  buildCart() {
    if (!this.order || !this.products.length) {
      this.endLoading()
      return
    }

    let deliveryInfoHtml = ''
    if (this.order.delivery) {
      const address = Address.fromAttributes(this.order.delivery_address)
      const { street, locality } = address
      deliveryInfoHtml = this.deliveryTemplate({ street, locality })
    }
    this.deliveryInfoTarget.innerHTML = deliveryInfoHtml

    let html = ''
    forEach(this.order.bookings, booking => {
      const total = this.moneyFormatter.format(booking.total_cents / 100)
      const itemDetails = this.buildBookingDetails(booking)
      const menu = this.buildItemMenu(booking, 'booking')
      html += this.cartItemTemplate({ itemDetails, total, menu })
    })
    forEach(this.order.line_items, item => {
      if (item.name === 'Beachy Fee' || item.name === 'Tip' || item.name === 'Promo Discount') return
      const itemDollars = divide(item.price_cents, 100)
      const total = this.moneyFormatter.format(itemDollars)
      const itemDetails = this.buildLineItemDetails(item)
      const menu = this.buildItemMenu(item, 'line_item')
      html += this.cartItemTemplate({ itemDetails, total, menu })
    });

    this.itemsTarget.innerHTML = html
    this.displayOrderTotals();

    this.toggleCheckoutButton();
    this.endLoading()
  }

  buildLineItemDetails(item) {
    const productName = this.getProduct(item.product_id)?.name
    return this.retailTemplate({ productName })
  }

  buildBookingDetails(booking) {
    const start = moment(booking.starts_at);
    const end = moment(booking.ends_at);
    const days = end.diff(start, 'days') + 1;
    const dayText = days > 1 ? 'days' : 'day';
    const pricePerDay = divide(divide(booking.total_cents, 100), days)
    const productName = this.getProduct(booking.product_id)?.name
    const dateRange = this.getDateRange(start, end)
    let description = `${ this.moneyFormatter.format(pricePerDay) }/day for ${ days } ${ dayText }`

    const { per_unit_discounted_price_cents, discounted_total_cents } = booking
    const quantity = divide(discounted_total_cents, per_unit_discounted_price_cents)

    if (days === 1 && quantity > 1) { // if only one day but multiple rentals, show the number times unit price
      description = `${quantity} x ${ this.moneyFormatter.format(divide(per_unit_discounted_price_cents, 100)) }`
    }

    return this.bookingTemplate({ productName, dateRange, description })
  }

  buildItemMenu(item, type) {
    let itemDetails = ''
    let itemPrice = ''
    let matchingOverrides = []

    if (type === 'line_item') {
      itemDetails = this.buildLineItemDetails(item)
      itemPrice = this.moneyFormatter.format(divide(item.price_cents, 100))
      matchingOverrides = filter(item.notes, ['meta.to_cents', item.price_cents])
    } else if (type === 'booking') {
      itemDetails = this.buildBookingDetails(item)
      itemPrice = this.moneyFormatter.format(divide(item.total_cents, 100))
      matchingOverrides = filter(item.notes, ['meta.to_cents', item.discounted_total_cents])
    }

    const lastMatchingOverride = last(sortBy(matchingOverrides, 'created_at'))
    const lastOverridePrice = lastMatchingOverride ? divide(lastMatchingOverride.meta.to_cents, 100) : ''
    const lastOverrideText = lastMatchingOverride ? lastMatchingOverride.reason : ''
    return this.itemMenuTemplate({ item, type, itemDetails, itemPrice, lastOverridePrice, lastOverrideText })
  }

  displayOrderTotals() {
    let orderDetails = { subtotal: '-', taxesAndFees: '-', discount: '-', promo: '-', tip: '-', total: '-' }
    if (this.order) {
      orderDetails = {
        subtotal: this.moneyFormatter.format(this.order.item_total_cents / 100),
        taxesAndFees: this.moneyFormatter.format((
          this.order.sales_tax_total_cents
            + this.order.processing_fee_total_cents
            + this.order.gratuity_total_cents
            + this.order.custom_fee_total_cents
        ) / 100),
        discount: this.moneyFormatter.format(this.order.discount_total_cents / 100),
        promo: this.moneyFormatter.format(this.order.promotion_discount_total_cents / 100),
        tip: this.moneyFormatter.format(this.order.tip_total_cents / 100),
        total: this.moneyFormatter.format(this.order.total_cents / 100),
      }
    }
    this.cartDetailTarget.innerHTML = this.cartDetailTemplate(orderDetails)

    if (!this.order) return

    if (this.order.discount_total_cents !== 0) {
      this.discountContainerTarget.classList.remove('hidden')
    } else {
      this.discountContainerTarget.classList.add('hidden')
    }
    if (this.order.promotion_discount_total_cents !== 0) {
      this.promoContainerTarget.classList.remove('hidden')
    } else {
      this.promoContainerTarget.classList.add('hidden')
    }
  }

  getDateRange(start, end) {
    if (this.convertToUtcTime(start) === this.convertToUtcTime(end)) {
      return this.convertToUtcTime(start);
    } else {
      return `${this.convertToUtcTime(start)}-${this.convertToUtcTime(end)}`
    }
  }

  convertToUtcTime(utcTime) {
    return moment.utc(utcTime).clone().format('l');
  }

  getOrder() {
    if (!this.orderId) {
      return Promise.resolve();
    }
    closeFlashAlert();
    this.startLoading()
    return fetch(
      `/sandbar/admin/orders/${this.orderId}`,
      {
        method: 'GET',
        headers: this.fetchHeaders,
      }
    )
      .then(resp => {
        if (resp.ok) {
          return resp.json()
        } else {
          resp.json().then(err => this.handleError(err))
        }
      })
      .then(order => {
        this.order = order;
        this.orderId = order?.id;
        this.destinationId = order?.destination_id;
        this.dispatchOrderLoaded()
        this.buildCart();
      })
      .catch(error => this.handleError(error));
  }

  getProduct(id) {
    const product = find(this.products, ['id', id])
    return product;
  }

  salableItems() {
    let salableLineItems = filter(this.order.line_items, item => item.name !== 'Beachy Fee' && item.name !== 'Tip' && item.name !== 'Promo Discount')
    let salableBookings = this.order.bookings
    return salableLineItems.concat(salableBookings)
  }

  removeFromCart = event => {
    const element = event.currentTarget
    const itemType = element.dataset.itemType
    const id = element.dataset.id
    closeFlashAlert();
    this.startLoading()

    fetch(`/sandbar/admin/orders/${this.orderId}/${itemType}s/${id}`, {
      headers: this.fetchHeaders,
      method: 'DELETE',
    })
      .then(response => {
        if (response.ok) {
          this.dispatchAmountChange();
        } else {
          response.json().then(err => this.handleError(err))
        }
      })
      .catch(err => this.handleError(err))
  }

  dispatchAmountChange = () => {
    this.getOrder()
      .then(() => {
        const amountChangeEvent = new CustomEvent('amountChange', {})
        this.contextContainer.dispatchEvent(amountChangeEvent)
      })
      .catch(err => this.handleError(err))
  }

  dispatchOrderLoaded = () => {
    const detail = {
      order: this.order,
      orderId: this.order?.id,
      destinationId: this.order?.destination_id,
      amount: this.order?.total_cents,
    }
    const orderLoadedEvent = new CustomEvent('orderLoaded', { detail })
    this.contextContainer.dispatchEvent(orderLoadedEvent)
  }

  cartCancel(event) {
    event.preventDefault();
    if (window.confirm('Are you sure that you want to abandon this order?')) { // eslint-disable-line no-alert

      this.startLoading()

      fetch(`/sandbar/admin/orders/${this.orderId}`, {
        headers: this.fetchHeaders,
        method: 'DELETE',
      })
        .then(resp => {
          if (resp.ok) {
            window.location.replace('/sandbar/admin/orders/new');
          } else {
            resp.json().then(err => {
              this.handleError(err)
              flashAlert('Something went wrong. Please reload the page')
            })
          }
        })
        .catch(err => this.handleError(err));
    }
  }

  isOrderEmpty() {
    return !this.order || !this.order.destination_id
  }

  toggleCheckoutButton() {
    if (this.salableItems().length > 0) {
      this.checkoutBtnTarget.classList.remove('disabled-link');
    } else {
      this.checkoutBtnTarget.classList.add('disabled-link');
    }
  }
}
